<script setup lang="ts">
import { isNull } from 'lodash'
import { ArrowDownCircleIcon, TrashIcon } from '@heroicons/vue/20/solid'
import { useMutation } from '@vue/apollo-composable'
import type { StatusResponse, User } from '@/components/users/interfaces.ts'
import {
  ProjectSettingsDeleteAssistantFile,
  ProjectSettingsDisableAssistantFile,
  ProjectSettingsEnableAssistantFile,
  UploadAiFilesMutation,
} from '@/components/projects/graphql/project.mutations.ts'
import { base64ToFile } from '@/utils/util.ts'
import type { ChatGptAssistantFile } from '@/components/projects/interfaces.ts'
import { ProjectAIType } from '@/enums.ts'
import { useToast } from 'vue-toastification'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import prettyBytes from 'pretty-bytes'
import { computed, ref } from 'vue'
import { faCheckCircle, faCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { ASSISTANT_AI_FILES_PREFIX } from '@/config/config.ts'
import { API_BASE_URL } from '@/config/env.const.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { FindUsersQuery } from '@/components/users/graphql/user.queries.ts'
import type { MutationUpdaterFunction } from '@apollo/client/core/types'
import ChatGptAssistantFiles from '@/components/projects/settings/assistant/ChatGptAssistantFiles.vue'
import { GetProjectChatGPTAssistantInfoAndModelsAndFiles } from '@/components/projects/graphql/project.queries.ts'
import type { ApolloCache } from '@apollo/client'
import { ApolloClient } from '@apollo/client/core'
import { apolloProvider } from '@/graphql.ts'

dayjs.extend(isToday)
dayjs.extend(customParseFormat)

// TODO: Limit filename length?

const isFileChangeRequestRunning = ref<boolean>(false)
const toast = useToast()
const props = defineProps<{
  projectId: number | null
  files: ChatGptAssistantFile[]
}>()
const files = computed<ChatGptAssistantFile[]>(() => {
  return props.files
})

// Mutations
const { t } = useTranslations()
const {
  onError: onFileEnableFailed,
  onDone: onFileEnableSucceed,
  mutate: enableAssistantFile
} = useMutation<{ file: ChatGptAssistantFile }>(ProjectSettingsEnableAssistantFile)
const {
  onError: onFileDisableFailed,
  onDone: onFileDisableSucceed,
  mutate: disableAssistantFile
} = useMutation<{ file: ChatGptAssistantFile }>(ProjectSettingsDisableAssistantFile)
const {
  onError: onFileDeletionFailed,
  onDone: onFileDeletionSucceed,
  mutate: deleteFile
} = useMutation<{ file: ChatGptAssistantFile }>(ProjectSettingsDeleteAssistantFile)
const {
  onError: onFileUploadingFailed,
  onDone: onFileUploadingDone,
  mutate: uploadFile,
} = useMutation<{ files: ChatGptAssistantFile[] }>(UploadAiFilesMutation)

// @ts-expect-error Jevgeni fix it
onFileDeletionSucceed(({ data: { file }}) => {
  toast.success(`File ${file.name} deleted`)
  setFileChangeRequestRunning(false)
})

onFileDeletionFailed((error) => {
  console.error('onFileInputChangedDoUpload | onError:', error)
  toast.error(error.message)
  setFileChangeRequestRunning(false)
})

// @ts-expect-error Jevgeni fix it
onFileEnableSucceed(({ data: { file }}) => {
  toast.success(`File «${file.name}» included`)
  setFileChangeRequestRunning(false)
})

onFileEnableFailed((error) => {
  console.error('onEnableFileButtonClicked | onError:', error)
  setFileChangeRequestRunning(false)
  toast.error(error.message)
})

// @ts-expect-error Jevgeni fix it
onFileDisableSucceed(({ data: { file }}) => {
  toast.warning(`«${file.name}» excluded`)
  setFileChangeRequestRunning(false)
})

onFileDisableFailed((error) => {
  toast.error(error.message)
  setFileChangeRequestRunning(false)
})

// @ts-expect-error Jevgeni fix it
onFileUploadingDone(({ data: { files }}) => {
  rewriteFilesInCache(files)
  toast.success('Upload succeed')
})

onFileUploadingFailed((error) => {
  console.error('onFileInputChangedDoUpload | onError:', error)
  toast.error(error.message)
})

function formatFileCreationDate(time: string): string {
  const instance = dayjs(time, 'YYYY-MM-DDTHH:mm:ss.000ZZ') // TODO: Config

  return instance.format('D MMM YYYY HH:mm')
}

function onAddFileButtonClicked(): void {
  document.getElementById('chatGptAssistantFiles')?.click()
}

function setFileChangeRequestRunning(flag: boolean): void {
  isFileChangeRequestRunning.value = flag
}

function updateFileStateInCache (
  proxy: ApolloCache<any>,
  file: ChatGptAssistantFile,
  deleteFile = false
): void {
  const info = proxy.readQuery<{ files: ChatGptAssistantFile[] }>({
    query: GetProjectChatGPTAssistantInfoAndModelsAndFiles,
    variables: { projectId: props.projectId },
  })
  const files = (info?.files ?? []) as ChatGptAssistantFile[]

  proxy.writeQuery({
    query: GetProjectChatGPTAssistantInfoAndModelsAndFiles,
    variables: { projectId: props.projectId },
    data: {
      ...info,
      files: deleteFile
        ? files.filter(({ key }) => file.key !== key)
        : files.map((fileCached) => file.key === fileCached.key ? file : fileCached)
    },
  })
}

function rewriteFilesInCache (
  files: ChatGptAssistantFile[]
): void {
  const info = apolloProvider.defaultClient.readQuery<{ files: User[] }>({
    query: GetProjectChatGPTAssistantInfoAndModelsAndFiles,
    variables: { projectId: props.projectId },
  })

  apolloProvider.defaultClient.writeQuery({
    query: GetProjectChatGPTAssistantInfoAndModelsAndFiles,
    variables: { projectId: props.projectId },
    data: {...info, files },
  })
}

async function onEnableFileButtonClicked(fileKey: string): Promise<void> {
  setFileChangeRequestRunning(true)
  await enableAssistantFile({
    projectId: props.projectId,
    aiType: ProjectAIType.ChatGPTAssistantAPI,
    fileKey,
  }, {
    // @ts-expect-error Jevgeni fix it
    update: (proxy, { data: { file } }) => updateFileStateInCache(proxy, file)
  })
}

function onDisableFileButtonClicked(fileKey: string): void {
  setFileChangeRequestRunning(true)
  disableAssistantFile({
    projectId: props.projectId,
    aiType: ProjectAIType.ChatGPTAssistantAPI,
    fileKey
  }, {
    // @ts-expect-error Jevgeni fix it
    update: (proxy, { data: { file } }) => updateFileStateInCache(proxy, file)
  })
}

function onDeleteFileButtonClicked(fileKey: string): void {
  setFileChangeRequestRunning(true)
  deleteFile({
    projectId: props.projectId,
    aiType: ProjectAIType.ChatGPTAssistantAPI,
    fileKey,
  }, {
    // @ts-expect-error Jevgeni fix it
    update: (proxy, { data: { file } }) => updateFileStateInCache(proxy, file, true)
  })
}

function onFileInputChangedDoUpload(event: Event): void {
  event.preventDefault() // Prevent whole form sending
  const input = event.target as HTMLInputElement

  uploadFile({
    projectId: props.projectId,
    aiType: ProjectAIType.ChatGPTAssistantAPI,
    req: input.files,
  })
}
</script>
<template>
  <h2 class="text-lg font-semibold text-gray-600 text-center">
    <span>{{ t('settings.primary.uploadFiles') }}</span>

    <button
      type="button"
      @click="onAddFileButtonClicked"
      class="ml-5 rounded-lg px-4 py-2 bg-indigo-500 text-xs font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
    >
      {{ t('common.buttons.add') }}
    </button>
    <input
      multiple
      id="chatGptAssistantFiles"
      type="file"
      class="hidden"
      @change="onFileInputChangedDoUpload"
    />
  </h2>
  <div class="px-4 sm:px-6 lg:px-8">
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr class="text-center">
                <th scope="col" class="py-3.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                  {{ t('settings.fileTable.name') }}
                </th>
                <th scope="col" class="px-3 py-3.5 text-sm font-medium text-gray-900">
                  {{ t('settings.fileTable.size') }}
                </th>
                <th scope="col" class="px-3 py-3.5 text-sm font-medium text-gray-900">
                  {{ t('settings.fileTable.created') }}
                </th>
                <th scope="col" class="px-3 py-3.5 text-sm font-medium text-gray-900">
                  {{ t('settings.fileTable.updated') }}
                </th>
                <th scope="col" class="relative py-3.5 pl-3 text-sm font-medium pr-4 sm:pr-3">
                  {{ t('settings.fileTable.actions') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="5" class="py-5 text-center" v-if="files.length === 0">
                  <span class="text-sm">
                    {{ t('settings.fileTable.ifYouUploadFilesTheAssistantWillUseTheAnswersFromThem') }}
                  </span>
                  <br />
                  <span>
                    {{ t('settings.fileTable.theFilesYouUpload') }}
                    <span class="font-medium text-orange-600">
                      {{ t('settings.fileTable.shouldNotContainPeoplesPersonalOrContactData') }}
                    </span>
                  </span>
                </td>
              </tr>
              <tr v-for="file in files" :key="file.key" class="text-center">
                <td
                  class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                >
                  <span
                    class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                    >{{ file.name }}</span
                  >
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <span
                    class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                  >
                    {{ prettyBytes(file?.sizeBytes ?? 0) }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <span
                    class="mt-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                  >
                    {{ formatFileCreationDate(file.createdBy!.date) }}
                  </span>
                  <br />
                  <span
                    class="mt-1 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                  >
                    {{ file.createdBy!.firstname }} {{ file.createdBy!.lastname }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <div v-if="isNull(file?.updatedBy)"></div>
                  <div v-else>
                    <span
                      class="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                    >
                      {{ formatFileCreationDate(file?.updatedBy?.date ?? '') }}
                    </span>
                    <br />
                    <span
                      class="mt-1 inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                    >
                      {{ file.createdBy?.firstname }} {{ file.updatedBy?.lastname }}
                    </span>
                  </div>
                </td>
                <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-sm font-medium sm:pr-3">
                  <a
                    target="_self"
                    :href="`${API_BASE_URL}${ASSISTANT_AI_FILES_PREFIX}${file.key}`"
                    type="button"
                    :class="{
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600': true,
                      'inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-2.5 py-1.5': true,
                      'text-sm text-center font-light text-white shadow-sm': true,
                      'hover:bg-green-700': true,
                    }"
                  >
                    <ArrowDownCircleIcon class="size-5" aria-hidden="true" />
                  </a>
                  &nbsp;
                  <button
                    v-if="file.options.chatGptIsIncluded"
                    @click="
                      (ev) => {
                        ev.preventDefault()
                        onDisableFileButtonClicked(file.key)
                      }
                    "
                    type="button"
                    :class="{
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600': true,
                      'inline-flex items-center gap-x-1.5 rounded-md bg-indigo-500 px-2.5 py-1.5': true,
                      'text-sm text-center font-light text-white shadow-sm': true,
                      'hover:bg-indigo-700': true,
                    }"
                  >
                    <FontAwesomeIcon :icon="faCheckCircle" class="-ml-0.5 size-5" />
                    <!-- setFileChangeRequestRunning -->
                  </button>
                  <button
                    v-else
                    @click="
                      (ev) => {
                        ev.preventDefault()
                        onEnableFileButtonClicked(file.key)
                      }
                    "
                    type="button"
                    :class="{
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600': true,
                      'inline-flex items-center gap-x-1.5 rounded-md bg-indigo-500 px-2.5 py-1.5': true,
                      'text-sm  text-center font-light text-white shadow-sm hover:bg-indigo-500': true,
                      'hover:bg-indigo-700': true,
                    }"
                  >
                    <FontAwesomeIcon :icon="faCircle" class="-ml-0.5 size-5" />
                  </button>
                  &nbsp;
                  <button
                    @click="
                      (ev) => {
                        ev.preventDefault()
                        onDeleteFileButtonClicked(file.key)
                      }
                    "
                    :key="`delete_${file.key}`"
                    type="button"
                    class="inline-flex items-center gap-x-1.5 rounded-md bg-red-500 px-2.5 py-1.5 text-sm font-light text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 text-center"
                  >
                    <TrashIcon class="-ml-0.5 size-5" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
